import { defaultGuid } from '@equifinance/shared';
import { Stages } from '../../types/Stages';
import { Account } from './Account';
import { FinancialYearSet, PartnershipFinancialYearSet, SoleTraderFinancialYearSet } from './IncomeAndEmployment';

export const defaultAccount: Account = {
  updateCount: 0,
  dirtyCount: 0,
  initiator: '',

  id: defaultGuid,
  errors: {},
  warnings: {},
  consentToSearch: '',
  consentToSubmit: {
    htmlText: '',
    moment: '',
  },
  productsResultDigest: null,
  stage: Stages.CreditDecision,
  status: 'WithBroker',
  brokerageName: '',
  applicants: [{
    id: defaultGuid,
    incomeAndEmployment: {
      id: defaultGuid,
      employmentHistory: [],
      otherIncomes: [],
      versionInfo: [],
    },
    personalDetails: {
      id: defaultGuid,
      title: 'Other',
      firstName: '',
      middleName: '',
      lastName: '',
      previousLastName: '',
      dateOfBirth: '',
      plannedRetirementAge: 0,
      maritalStatus: 'Single',
      emailAddress: '',
      homeNumber: '',
      mobileNumber: '',
      nationality: '',
      countryOfResidence: '',
      citizenshipStatus: 'BritishCitizen',
      planToPayLoanRetire: 'Other',
      isLendingIntoRetirement: false,
    },
    addresses: [],
    contacts: {
      id: defaultGuid,
      contactDetails: [],
    },
    requiresAdditionalSupport: false,
    additionalSupportDetails: {
      id: defaultGuid,
      supportTypes: [],
      supportTypeDetails: '',
    },
  }],
  securityAddress: {
    id: defaultGuid,
    subBuilding: '',
    houseName: '',
    houseNumber: '',
    street: '',
    postTown: '',
    postCode: '',
    country: '',
    region: '',
  },
  rentalIncomeAndExpenditures: [],
  income: {
    id: defaultGuid,
    version: 0,
    totalHouseholdGrossMonthlyIncome: 0,
    totalHouseholdNetMonthlyIncome: 0,
  },
  householdCompositions: {
    id: defaultGuid,
    numberOfDependentsUnder18: 0,
    numberOfDependentsAbove18: 0,
    listChildrenUnder18: [],
    otherOccupants: [],
  },
  expenditure: {
    id: defaultGuid,
    enabledExpenditureFields: [],
    essentialExpenditure: 0,
    councilTax: 0,
    nonEssentialExpenditure: 0,
    transportExpenditure: 0,
    declaredCouncilTaxAboveMinimumTolerance: false,
    essentialExpenditureTooltip: [],
    nonEssentialExpenditureTooltip: [],
    insurances: 0,
    version: 0,
  },
  property: {
    id: defaultGuid,
    propertyValue: 0,
    wasBoughtThroughSharedOwnershipOrHelpToBuy: false,
    purchasingTheFullShareWithThisLoanAdvance: false,
    estimatedPropertyValue: 0,
    propertyValueType: 'BasedOnAvm',
    passesValuationCriteria: false,
    isExCouncil: false,
    isRtbPurchase: false,
    purchasePrice: 0,
    amountOfDiscount: 0,
    dateOfPurchase: '',
    propertyType: 'ConvertedFlatMaisonette',
    bedrooms: 0,
    livingRooms: 0,
    floorsInBlock: 0,
    constructionType: 'SystemBuilt',
    isPropertyAboveCommercialPremises: false,
    tenure: 'Leasehold',
    monthlyServiceCharge: 0,
    remainingLease: 0,
    monthlyGroundRent: 0,
    isUpdateWithMonthlyGroundRent: false,
    isUpdateWithMonthlyServiceCharge: false,
    isParking: false,
  },
  mortgage: {
    id: defaultGuid,
    outstandingBalance: 0,
  },
  loanDetails: {
    id: defaultGuid,
    loanPurpose: [],
    loanPurposeDetails: '',
    loanAmount: 0,
    repaymentType: 'CapitalAndRepaymentOnly',
    grossLoanAmount: 0,
    isExistingCustomer: false,
    term: 0,
    isConsolidateExistingLoan: false,
    outstandingCurrentBalance: 0,
    estimatedRedemptionAmount: 0,
    fees: {
      BrokerFee: {
        id: defaultGuid,
        fee: 0,
        isAddToLoan: false,
      },
      ValuationFee: {
        id: defaultGuid,
        fee: 0,
        isAddToLoan: false,
      },
      AdviceFee: {
        id: defaultGuid,
        fee: 0,
        isAddToLoan: false,
      },
      LenderFee: {
        id: defaultGuid,
        fee: 0,
        isAddToLoan: false,
      },
      FeesToBePaidUpFront: {
        id: defaultGuid,
        fee: 0,
        isAddToLoan: false,
      },
    },
  },
  disclosure: {
    id: defaultGuid,
    anyCriminalConvictions: false,
    anyPreviousPropertyRepossessed: false,
  },
  dmpDetails: {
    id: defaultGuid,
    everBeenInDMP: false,
    dateLastEnterDMP: '',
    currentlyInDMP: false,
    dateLastExitDMP: '',
  },
  advisorDetails: {
    id: defaultGuid,
    wasResponsibleForAdvice: false,
    fcaNumber: '',
    registeredCompanyName: '',
    webAddress: '',
    emailAddress: '',
    telephoneNumber: '',
    advisorAddress: {
      id: defaultGuid,
      subBuilding: '',
      houseName: '',
      houseNumber: '',
      street: '',
      postTown: '',
      postCode: '',
      country: '',
      region: '',
    },
  },
  directDebit: {
    id: defaultGuid,
    bankDetails: {
      id: defaultGuid,
      accountNumber: '',
      sortCode: '',
      nameOnAccount: [],
    },
    canVerify: false,
    preferredDueDate: 0,
  },
  onsVersionData: {
    nonEssentialExpenditure: 0,
    essentialExpenditure: 0,
  },
  isAmountMoreThanExpectedLevel: false,
  applicationNumber: null,
};

export const defaultFinancialYearSet: FinancialYearSet = {
  id: defaultGuid,
  latestCompletedYear: {
    id: defaultGuid,
    financialYearEnd: null,
    grossAnnualSalary: null,
    annualDividends: null,
    netMonthlyTakeHome: null,
  },
  previousCompletedYear: {
    id: defaultGuid,
    financialYearEnd: null,
    grossAnnualSalary: null,
    annualDividends: null,
  },
  currentYearProjection: {
    id: defaultGuid,
    financialYearEnd: null,
    grossAnnualSalary: null,
    annualDividends: null,
  },
};

export const defaultSoleTraderFinancialYearSet: SoleTraderFinancialYearSet = {
  id: defaultGuid,
  latestCompletedYear: {
    id: defaultGuid,
    financialYearEnd: null,
    grossProfitBeforeTax: null,
    netProfitAfterTax: null,
    netMonthlyTakeHome: null,
  },
  previousCompletedYear: {
    id: defaultGuid,
    financialYearEnd: null,
    grossProfitBeforeTax: null,
    netProfitAfterTax: null,
  },
  currentYearProjection: {
    id: defaultGuid,
    financialYearEnd: null,
    grossProfitBeforeTax: null,
    netProfitAfterTax: null,
  },
};

export const defaultPartnershipFinancialYearSet: PartnershipFinancialYearSet = {
  id: defaultGuid,
  latestCompletedYear: {
    id: defaultGuid,
    financialYearEnd: null,
    totalGrossProfitBeforeTax: null,
    totalNetProfitAfterTax: null,
    customerNetMonthlyTakeHome: null,
  },
  previousCompletedYear: {
    id: defaultGuid,
    financialYearEnd: null,
    totalGrossProfitBeforeTax: null,
    totalNetProfitAfterTax: null,
  },
  currentYearProjection: {
    id: defaultGuid,
    financialYearEnd: null,
    totalGrossProfitBeforeTax: null,
    totalNetProfitAfterTax: null,
  },
};
